import { flattenError, z } from 'zod';

const PasswordRegex = /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[^A-Za-z\d])[\S\s]{8,64}$/;

// zod's own error messages are plain strings with no i18n hook, and this schema is shared by
// Signup.tsx, SetNewPassword.tsx, and Security.tsx.
export const PasswordValidation = z
  .string()
  .min(8, 'tooShort')
  .max(64, 'tooLong')
  .regex(/[a-z]/, 'missingLowercase')
  .regex(/[0-9]/, 'missingNumber')
  .regex(/[A-Z]/, 'missingUppercase')
  .regex(/[^A-Za-z0-9]/, 'missingSpecialChar')
  .regex(PasswordRegex, 'invalid');

type TranslateFn = (key: string, options?: { defaultValue?: string }) => string;

// Shared by every password form's `Form.Message match` check. Returns the translated message for
// the first rule the password fails, or null if it's valid. `fallbackKey` covers the (normally
// unreachable) case where the failure key doesn't match a known `passwordValidation.*` entry.
export function getPasswordValidationMessage(value: string, t: TranslateFn, fallbackKey: string): string | null {
  const result = PasswordValidation.safeParse(value);
  if (result.success) return null;

  const key = flattenError(result.error).formErrors[0];
  return t(`passwordValidation.${key}`, { defaultValue: t(fallbackKey) });
}

export function revalidateFields(refs: (HTMLInputElement | null)[]) {
  for (const el of refs) {
    el?.dispatchEvent(new Event('change', { bubbles: true }));
  }
}
